<script setup>
import { computed, reactive, shallowRef, watch } from 'vue';
import isSameDay from 'date-fns/isSameDay';
import isToday from 'date-fns/isToday';
import isYesterday from 'date-fns/isYesterday';
import format from 'date-fns/format';
import { ptBR } from 'date-fns/locale';
import { useI18n } from 'vue-i18n';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import {
  albumAttachmentsFor,
  hiddenAlbumMemberIds,
} from 'dashboard/helper/albumGrouping';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import { buildReplyFetchParams } from 'dashboard/helper/replyMessageHelper';
import {
  classifyMessageListChange,
  messageEntryDirection,
  messageRenderKey,
  runMessageEntryMotion,
} from './messageEntryMotion';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Boolean} animateNewMessages - Whether V2 append motion is enabled
 * @property {Number} conversationId - Current conversation identity
 * @property {Boolean} messageHistoryReady - Whether initial history is loaded
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  animateNewMessages: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: Number,
    required: true,
  },
  messageHistoryReady: {
    type: Boolean,
    default: true,
  },
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['retry']);
const { t } = useI18n();

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

// M9: o anúncio do álbum fica visível; as mídias seguintes somem do fio
// e entram na grade do cartão.
const albumHiddenIds = computed(() => hiddenAlbumMemberIds(allMessages.value));
const visibleMessages = computed(() =>
  allMessages.value.filter(message => !albumHiddenIds.value.has(message.id))
);

const messageEntryState = shallowRef(null);
const enteringMessageKeys = shallowRef(new Set());

watch(
  [
    () => props.conversationId,
    () => props.animateNewMessages,
    () => props.messageHistoryReady,
    () => visibleMessages.value.map(messageRenderKey),
  ],
  () => {
    const { nextState, enteringKeys } = classifyMessageListChange({
      previousState: messageEntryState.value,
      conversationId: props.conversationId,
      messages: visibleMessages.value,
      enabled: props.animateNewMessages,
      historyReady: props.messageHistoryReady,
    });
    messageEntryState.value = nextState;
    enteringMessageKeys.value = new Set(enteringKeys);
  },
  { immediate: true, flush: 'sync' }
);

const messageEntryBinding = message => ({
  animate: enteringMessageKeys.value.has(messageRenderKey(message)),
  direction: messageEntryDirection(message),
  key: messageRenderKey(message),
});

const vMessageEntry = {
  mounted(element, { value }) {
    if (!value.animate) return;

    runMessageEntryMotion(element, value.direction);
    const remainingKeys = new Set(enteringMessageKeys.value);
    remainingKeys.delete(value.key);
    enteringMessageKeys.value = remainingKeys;
  },
};

const messageListKey = message =>
  props.animateNewMessages ? messageRenderKey(message) : message.id;

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls.
const fetchedReplyMessages = reactive(new Map());
const pendingReplyMessages = reactive(new Set());
const replyFetchAttempts = reactive({});
const REPLY_FETCH_RETRY_DELAY = 3000;
const MAX_REPLY_FETCH_ATTEMPTS = 4;

const hasCachedReplyMessage = cacheKey => fetchedReplyMessages.has(cacheKey);

const replyCacheKey = ({ messageId, sourceId }) => {
  const numericMessageId = Number(messageId);
  if (Number.isInteger(numericMessageId) && numericMessageId > 0) {
    return `id:${numericMessageId}`;
  }

  return sourceId ? `source:${sourceId}` : null;
};

const messageSourceId = message => message?.sourceId ?? message?.source_id;

const messageMatchesReply = (message, { messageId, sourceId }) => {
  const numericMessageId = Number(messageId);
  const hasMessageId =
    Number.isInteger(numericMessageId) && numericMessageId > 0;

  if (hasMessageId && Number(message.id) === numericMessageId) return true;
  return !!sourceId && messageSourceId(message) === sourceId;
};

const cacheReplyMessage = (replyReference, message) => {
  const cacheKey = replyCacheKey(replyReference);
  if (cacheKey) fetchedReplyMessages.set(cacheKey, message);

  if (message?.id) fetchedReplyMessages.set(`id:${message.id}`, message);
  if (messageSourceId(message)) {
    fetchedReplyMessages.set(`source:${messageSourceId(message)}`, message);
  }
};

/**
 * Fetches a specific message from the API using a local ID or source ID.
 * @param {Object} replyReference - The message reference to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (replyReference, conversationId) => {
  const cacheKey = replyCacheKey(replyReference);
  if (!cacheKey) return null;

  if (hasCachedReplyMessage(cacheKey)) {
    return fetchedReplyMessages.get(cacheKey);
  }

  if (pendingReplyMessages.has(cacheKey)) return null;
  pendingReplyMessages.add(cacheKey);

  try {
    // Prefer source_id for API fetch so quoted messages in prior conversations
    // of the same contact/inbox can be resolved (Meta Cloud self-replies).
    const requestParams = buildReplyFetchParams(replyReference, conversationId);
    if (!requestParams) {
      fetchedReplyMessages.set(cacheKey, null);
      return null;
    }

    const response = await MessageApi.getPreviousMessages({
      ...requestParams,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg =>
      messageMatchesReply(msg, replyReference)
    );

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      cacheReplyMessage(replyReference, camelCaseMessage);
      delete replyFetchAttempts[cacheKey];
      return camelCaseMessage;
    }

    if (replyReference.sourceId) {
      const nextAttempt = (replyFetchAttempts[cacheKey] || 0) + 1;
      replyFetchAttempts[cacheKey] = nextAttempt;

      if (nextAttempt < MAX_REPLY_FETCH_ATTEMPTS) {
        setTimeout(() => {
          fetchReplyMessage(replyReference, conversationId);
        }, REPLY_FETCH_RETRY_DELAY);
        return null;
      }
    }

    fetchedReplyMessages.set(cacheKey, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(cacheKey, null);
    return null;
  } finally {
    pendingReplyMessages.delete(cacheKey);
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const replyReference = {
    messageId:
      parentMessage.contentAttributes?.inReplyTo ??
      parentMessage.content_attributes?.in_reply_to,
    sourceId:
      parentMessage.contentAttributes?.inReplyToExternalId ??
      parentMessage.content_attributes?.in_reply_to_external_id,
  };

  if (!replyReference.messageId && !replyReference.sourceId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg =>
    messageMatchesReply(msg, replyReference)
  );

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(msg =>
      messageMatchesReply(msg, replyReference)
    );
  }

  // Then check fetch cache
  const cacheKey = replyCacheKey(replyReference);
  if (!replyMessage && cacheKey && hasCachedReplyMessage(cacheKey)) {
    replyMessage = fetchedReplyMessages.get(cacheKey);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id && cacheKey) {
    fetchReplyMessage(replyReference, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};

// SiteUp V2 (G4): .daychip — separador de dia no fio (mockup 03).
const messageDate = message => {
  if (!message?.createdAt && !message?.created_at) return null;
  const raw = message.createdAt ?? message.created_at;
  let ms;
  if (typeof raw === 'number') {
    ms = raw > 1e12 ? raw : raw * 1000;
  } else {
    ms = new Date(raw).getTime();
  }
  if (!Number.isFinite(ms)) return null;
  return new Date(ms);
};

const shouldShowDayChip = index => {
  const list = visibleMessages.value;
  const current = messageDate(list[index]);
  if (!current) return false;
  if (index === 0) return true;
  const prev = messageDate(list[index - 1]);
  if (!prev) return true;
  return !isSameDay(current, prev);
};

const dayChipLabel = message => {
  const date = messageDate(message);
  if (!date) return '';
  // Reusa labels já existentes (INTERNAL_CHAT) — sem inventar copy.
  if (isToday(date)) return t('INTERNAL_CHAT.DATE_BADGE_TODAY');
  if (isYesterday(date)) return t('INTERNAL_CHAT.DATE_BADGE_YESTERDAY');
  // Sem locale o date-fns cai no inglês: aparecia "05 Aug 2026" ao lado de
  // "3 de ago. · 15:54" na mesma tela.
  return format(date, "d 'de' MMM 'de' yyyy", { locale: ptBR });
};
</script>

<template>
  <!-- SiteUp V2 (G4): gap 8px entre bolhas (mb-2 no Message) + daychip central -->
  <ul class="flex flex-col gap-0 px-4 py-5 bg-n-surface-1">
    <slot name="beforeAll" />
    <template
      v-for="(message, index) in visibleMessages"
      :key="messageListKey(message)"
    >
      <li
        v-if="shouldShowDayChip(index)"
        class="list-none flex justify-center self-center my-2"
      >
        <span
          class="inline-flex items-center rounded-full bg-n-solid-1 px-3 py-1 text-xs leading-none text-n-slate-11"
        >
          {{ dayChipLabel(message) }}
        </span>
      </li>
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-message-entry="messageEntryBinding(message)"
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, visibleMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        :album-attachments="albumAttachmentsFor(message, allMessages)"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
